import { useState } from 'react';
import { Link } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { FilterSelect, Pill } from '@/components/inputs';
import { DEMON_TERMS, WRAITH_TERMS, DEMON_FEATURES, WRAITH_FEATURES, MAX_CONNECTIONS, PLANS, TELLY, PAYMENT_METHODS, planPriceList, quote, money } from '@/shared/pricing';
import { rows } from '@/shared/data';
import { loadProducts } from '@/shared/catalog';
import { builtInState } from '@/shared/products';
import { productSchema, useJsonLd } from '@/shared/seo';
import { formatIn, currencyLabel } from '@/shared/currency';
import { cn } from '@/lib/utils';
import { GLOW, LABEL, WRAP } from '@/components/home/styles';
import { useT } from '@/shared/i18n';

const { eq } = window.ApperSDK.CoreSDK;

function useCurrencies() {
  const { data } = useFetch(
    () =>
      sdk
        .table('currencies_c')
        .select(['Name', 'symbol_c', 'rate_c', 'active_c'])
        .where(eq('active_c', true))
        .orderBy('Name')
        .limit(100)
        .fetch()
        .then(rows)
        .then(uniqueByCode),
    [],
  );
  return data ?? [];
}

/** One entry per currency code, even if the table briefly holds a copy. */
function uniqueByCode(list) {
  const seen = new Set();
  return list.filter((c) => {
    const code = String(c.Name || '').toUpperCase();
    if (!code || seen.has(code)) return false;
    seen.add(code);
    return true;
  });
}

/** Product rows for the built-in plans; ready once loaded (or failed, which shows everything). */
function useBuiltIns() {
  const { data, error } = useFetch(loadProducts, []);
  return { products: data ?? [], ready: data !== undefined || !!error };
}

function approx(usd, cur) {
  if (!cur || cur.Name === 'USD' || !cur.rate_c) return null;
  return `≈ ${formatIn(usd, cur)} ${cur.Name}`;
}

function PlanFeatures({ conns, features }) {
  const { t } = useT();
  const items = [t('{count} simultaneous screens', { count: conns }), ...features.map((f) => t(f))];
  return (
    <ul className="mt-8 space-y-2.5 text-[15px] text-foreground/80">
      {items.map((f) => (
        <li key={f} className="flex items-center gap-3">
          <ApperIcon name="Check" size={16} className="text-primary" />
          {f}
        </li>
      ))}
    </ul>
  );
}

function PriceLine({ price, termLabelText, months, cur }) {
  const { t } = useT();
  const alt = approx(price, cur);
  return (
    <div className="mt-10 flex flex-wrap items-end gap-4">
      <span className={cn('text-7xl font-light leading-none', GLOW)}>{money(price)}</span>
      <span className="pb-2 text-sm text-muted-foreground">
        USD · {t(termLabelText)}
        {months > 0 && (
          <>
            <br />≈ {t('{price}/mo', { price: money(Math.round((price / months) * 100) / 100) })}
          </>
        )}
        {alt && (
          <>
            <br />
            {alt}
          </>
        )}
      </span>
    </div>
  );
}

export function Plans() {
  const { t } = useT();
  const currencies = useCurrencies();
  const [curCode, setCurCode] = useState('all');
  const cur = currencies.find((c) => c.Name === curCode);

  const [conns, setConns] = useState(1);
  const [demonTerm, setDemonTerm] = useState('1y');
  const [wraithTerm, setWraithTerm] = useState('12m');

  const demonT = DEMON_TERMS.find((t) => t.id === demonTerm);
  const wraithT = WRAITH_TERMS.find((t) => t.id === wraithTerm);
  const demonPrice = quote('Demon', demonTerm, conns);
  const wraithPrice = quote('Wraith', wraithTerm, 1);

  const { products, ready } = useBuiltIns();
  const showDemon = builtInState(products, 'Demon').onHome;
  const showWraith = builtInState(products, 'Wraith').onHome;
  const both = showDemon && showWraith;

  useJsonLd(
    'plans',
    ready && [
      showDemon &&
        productSchema({
          name: `${PLANS.Demon.name} IPTV plan`,
          description: `${PLANS.Demon.tagline}. ${PLANS.Demon.blurb}`,
          path: '/p/demon',
          prices: planPriceList('Demon'),
        }),
      showWraith &&
        productSchema({
          name: `${PLANS.Wraith.name} IPTV plan`,
          description: `${PLANS.Wraith.tagline}. ${PLANS.Wraith.blurb}`,
          path: '/p/wraith',
          prices: planPriceList('Wraith'),
        }),
    ].filter(Boolean),
  );

  if (!ready) return <section id="plans" aria-busy="true" className={cn(WRAP, 'min-h-[70vh] scroll-mt-24 pt-36')} />;
  if (!showDemon && !showWraith) return null;

  return (
    <section id="plans" className={cn(WRAP, 'scroll-mt-24 pt-36')}>
      <div className="flex flex-col gap-6 md:flex-row md:items-end md:justify-between">
        <div>
          <p className={LABEL}>{t('Choose your haunting')}</p>
          <h2 className="mt-4 text-[clamp(38px,6vw,72px)] font-light leading-[1.02]">
            {both ? t('Two spirits.') : t('One spirit.')}
            <br />
            <span className="text-muted-foreground">
              {both ? t('Pick the one that follows you home.') : t('Ready to follow you home.')}
            </span>
          </h2>
        </div>
        {currencies.length > 0 && (
          <div className="w-full sm:w-56">
            <p className="mb-2 text-xs text-muted-foreground">{t('Show approximate prices in')}</p>
            <FilterSelect
              value={curCode}
              onChange={setCurCode}
              allLabel={t('US dollars only')}
              values={currencies.filter((c) => c.Name !== 'USD').map((c) => c.Name)}
              labels={Object.fromEntries(currencies.map((c) => [c.Name, currencyLabel(c)]))}
              className="sm:w-56"
            />
          </div>
        )}
      </div>

      <div className={cn('mt-16 grid gap-16 md:gap-14', both ? 'md:grid-cols-2' : 'max-w-2xl')}>
        {showDemon && (
          <div className="border-t border-border pt-10">
            <div className="flex items-baseline justify-between gap-4">
              <h3 className="font-heading text-6xl leading-none">{PLANS.Demon.name}</h3>
              <span className={cn(LABEL, 'text-destructive')}>{t('1 – 5 connections')}</span>
            </div>
            <p className="mt-3 text-lg text-foreground/85">{t(PLANS.Demon.tagline)}</p>
            <p className="mt-3 max-w-md text-[15px] leading-7 text-muted-foreground">{t(PLANS.Demon.blurb)}</p>

            <p className={cn(LABEL, 'mt-10')}>{t('Connections')}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {Array.from({ length: MAX_CONNECTIONS }, (_, i) => i + 1).map((n) => (
                <Pill key={n} on={conns === n} onClick={() => setConns(n)}>
                  {n}
                </Pill>
              ))}
            </div>
            <p className={cn(LABEL, 'mt-8')}>{t('Term')}</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {DEMON_TERMS.map((term) => (
                <Pill key={term.id} on={demonTerm === term.id} onClick={() => setDemonTerm(term.id)}>
                  {t(term.label)}
                </Pill>
              ))}
            </div>
            <PriceLine price={demonPrice} termLabelText={demonT.label} months={demonT.months} cur={cur} />
            <PlanFeatures conns={conns} features={DEMON_FEATURES} />
            <Button size="lg" className="mt-10" asChild>
              <Link to={`/checkout?plan=Demon&term=${demonTerm}&connections=${conns}`}>{t('Summon the Demon')}</Link>
            </Button>
          </div>
        )}

        {showWraith && (
          <div className="border-t border-border pt-10">
            <div className="flex items-baseline justify-between gap-4">
              <h3 className="font-heading text-6xl leading-none">{PLANS.Wraith.name}</h3>
              <span className={LABEL}>{t('Single connection')}</span>
            </div>
            <p className="mt-3 text-lg text-foreground/85">{t(PLANS.Wraith.tagline)}</p>
            <p className="mt-3 max-w-md text-[15px] leading-7 text-muted-foreground">{t(PLANS.Wraith.blurb)}</p>

            <p className={cn(LABEL, 'mt-10')}>{t('Subscription package')}</p>
            <div className="mt-3 divide-y divide-border border-y border-border">
              {WRAITH_TERMS.map((term) => (
                <button
                  key={term.id}
                  type="button"
                  aria-pressed={wraithTerm === term.id}
                  onClick={() => setWraithTerm(term.id)}
                  className={cn(
                    'flex w-full items-center justify-between gap-3 py-4 text-start transition-colors',
                    wraithTerm === term.id ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <span className="flex items-center gap-3 text-[17px]">
                    <span
                      className={cn(
                        'h-2.5 w-2.5 rounded-full border',
                        wraithTerm === term.id ? 'border-primary bg-primary' : 'border-muted-foreground',
                      )}
                    />
                    {t(term.label)}
                    {term.epg && (
                      <span className="rounded-full border border-highlight/40 bg-highlight/15 px-2 py-0.5 text-[11px] text-highlight">
                        + EPG
                      </span>
                    )}
                  </span>
                  <span className="text-[17px]">{money(term.price)}</span>
                </button>
              ))}
            </div>
            <PriceLine price={wraithPrice} termLabelText={wraithT.label} months={wraithT.months} cur={cur} />
            <p className="mt-4 text-sm text-muted-foreground">
              {wraithT.epg
                ? t('Includes the premium EPG: now and next, with show art and descriptions.')
                : t('Choose 6 or 12 months to include the premium EPG.')}
            </p>
            <PlanFeatures conns={1} features={WRAITH_FEATURES} />
            <Button size="lg" className="mt-10" asChild>
              <Link to={`/checkout?plan=Wraith&term=${wraithTerm}&connections=1`}>{t('Summon the Wraith')}</Link>
            </Button>
          </div>
        )}
      </div>
      {both && (
        <div className="mt-14 flex flex-col gap-4 rounded-lg border border-border bg-card p-6 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-lg">{t('Not sure which spirit is yours?')}</p>
            <p className="text-sm text-muted-foreground">
              {t('Answer five quick questions, or see {a} and {b} side by side.', {
                a: PLANS.Wraith.name,
                b: PLANS.Demon.name,
              })}
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button asChild>
              <Link to={{ pathname: '/compare', hash: '#quiz' }}>{t('Take the quiz')}</Link>
            </Button>
            <Button variant="outline" asChild>
              <Link to="/compare">{t('Compare plans')}</Link>
            </Button>
          </div>
        </div>
      )}
      <p className="mt-12 text-sm text-muted-foreground">
        <span className={cn(LABEL, 'me-3')}>{t('We accept')}</span>
        {PAYMENT_METHODS.map((p) => t(p.label)).join('  ·  ')}
      </p>
    </section>
  );
}

export function TellySection() {
  const { t } = useT();
  const [qty, setQty] = useState(1);
  const licences = t('{count} licences', { count: qty });
  const { products, ready } = useBuiltIns();
  const show = ready && builtInState(products, 'Telly').onHome;

  useJsonLd(
    'telly',
    show &&
      productSchema({
        name: TELLY.name,
        description: `${TELLY.termLabel}. ${PLANS.Telly.blurb}`,
        path: '/p/desktop-telly',
        prices: planPriceList('Telly'),
      }),
  );

  if (!show) return null;

  return (
    <section id="desktop-telly" className={cn(WRAP, 'scroll-mt-24 pt-28')}>
      <div className="grid gap-12 border-t border-border pt-10 md:grid-cols-[1.1fr_1fr] md:gap-16">
        <div>
          <p className={cn(LABEL, 'flex items-center gap-2')}>
            <ApperIcon name="Monitor" size={13} />
            {t('Player licence')}
          </p>
          <h3 className="mt-4 text-[clamp(34px,5vw,56px)] font-light leading-[1.05]">
            {TELLY.name}
            <span className="block text-muted-foreground">{t('One player. Every screen you own.')}</span>
          </h3>
          <p className="mt-5 max-w-md text-[15px] leading-7 text-muted-foreground">
            {t(
              'A clean, fast player app for your Ghost Cable line. Buy it here on its own, or tick it as an add-on when you order a plan. Need it on more screens at once? Get one licence per device.',
            )}
          </p>
          <ul className="mt-8 space-y-2.5 text-[15px] text-foreground/80">
            {TELLY.points.map((f) => (
              <li key={f} className="flex items-center gap-3">
                <ApperIcon name="Check" size={16} className="text-primary" />
                {t(f)}
              </li>
            ))}
          </ul>
        </div>
        <div className="flex flex-col justify-end">
          <p className={LABEL}>{t('Licences')}</p>
          <div className="mt-3 flex items-center gap-3">
            <Button
              variant="outline"
              size="icon"
              aria-label="One fewer licence"
              disabled={qty <= 1}
              onClick={() => setQty((q) => Math.max(1, q - 1))}
            >
              <ApperIcon name="Minus" size={16} />
            </Button>
            <span className="w-8 text-center text-xl tabular-nums">{qty}</span>
            <Button
              variant="outline"
              size="icon"
              aria-label="One more licence"
              disabled={qty >= TELLY.maxQty}
              onClick={() => setQty((q) => Math.min(TELLY.maxQty, q + 1))}
            >
              <ApperIcon name="Plus" size={16} />
            </Button>
            <span className="text-sm text-muted-foreground">
              {t('{count} devices at the same time', { count: qty })}
            </span>
          </div>
          <div className="mt-8 flex items-end gap-4">
            <span className={cn('text-7xl font-light leading-none', GLOW)}>{money(TELLY.price * qty)}</span>
            <span className="pb-2 text-sm text-muted-foreground">
              USD · {licences}
              <br />
              {qty > 1 ? `${t('{price} each', { price: money(TELLY.price) })} · ` : ''}{t('one-time payment')}
            </span>
          </div>
          <div className="mt-8 flex flex-wrap items-center gap-5">
            <Button size="lg" asChild>
              <Link to={`/checkout?plan=Telly&term=lifetime&connections=${qty}`}>
                {t('Get {name}', { name: qty > 1 ? licences : TELLY.name })}
              </Link>
            </Button>
            <a
              href={TELLY.url}
              target="_blank"
              rel="noreferrer"
              className="text-sm underline-offset-4 hover:text-primary hover:underline"
            >
              {t('About the app')}
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
